import React, { useState } from 'react'
import { ControlledTextField } from 'transfix-form/components'
import { Button } from 'transfix-ui/components/Button'
import { Spinner } from 'transfix-ui/components/Spinner'
import { useGlobalToast } from 'transfix-ui/components/Toast/GlobalToast'
import { Box, Typography } from 'transfix-ui/core'
import { IPriceRequestDetailsFragment } from 'apps/marketplace/pages/spot-quote-tool/graphql/PriceRequestDetailsFragment'
import { ISpotRate } from 'apps/marketplace/store/amQuoting/types'
import { EditIcon } from 'transfix-icons'
import { IconButton, Card, CardHeader, brandColors } from 'transfix-ui'
import { ControlledButtonGroup, IButtonValueProps } from './ControlledButtonGroup'
import { QuoteChanges } from './QuoteChanges'
import { usePriceRequestVersions } from './hooks/usePriceRequestVersions'
import { useUpdateQuote } from './hooks/useUpdateQuote'
import { useUpdateQuoteForm } from './hooks/useUpdateQuoteForm'
import { TUpdateQuoteSchema } from './schema/UpdateQuoteSchema'

interface ISubmittedQuoteCardProps {
  className?: string
  priceRequest: IPriceRequestDetailsFragment
  onSubmit?: (value: number, allIn: boolean, lineHaul: boolean) => void
  spotRate: Partial<ISpotRate> & {
    priceRequestId: ISpotRate['priceRequestId']
  }
}

export const SubmittedQuoteCard = ({
  className,
  onSubmit,
  priceRequest,
  spotRate,
}: ISubmittedQuoteCardProps) => {
  const priceRequestId = spotRate.priceRequestId.toString()

  const defaultValues = {
    id: priceRequestId,
    customerQuoteType: 'all_in',
  }

  const { control, getValues, handleSubmit, watch } = useUpdateQuoteForm({ defaultValues })
  const { loading, update } = useUpdateQuote()
  const { versions: submittedQuoteVersions } = usePriceRequestVersions(priceRequest.id)

  const isMissingHistory =
    priceRequest.customerQuote === null || priceRequest.customerQuote === undefined

  const [isEditing, setIsEditing] = useState<boolean>(isMissingHistory)

  const { openToast } = useGlobalToast()

  const updateQuote = async (formValues: TUpdateQuoteSchema) => {
    try {
      const submitFormValues = { ...formValues, id: priceRequestId }
      const { errors, updateQuoteResult } = await update(submitFormValues)

      if (errors && errors.length > 0) {
        const errorMessage = errors.map(error => error.message).join(', ')
        openToast({
          toastMessage: `Errors applying updates: ${errorMessage}. Contact FA Engineering`,
          toastType: 'alert',
        })
      } else {
        openToast({
          toastMessage: `Successfully updated price request ${updateQuoteResult.id}`,
          toastType: 'default',
        })
        setIsEditing(false)
        if (onSubmit) {
          onSubmit(
            submitFormValues.customerQuote,
            submitFormValues.customerQuoteType === 'linehaul',
            submitFormValues.customerQuoteType === 'all_in'
          )
        }
      }
    } catch (error) {
      openToast({
        toastMessage: 'There was an error updating quote, contact FA Engineering',
        toastType: 'alert',
      })
    }
  }

  const toggleEditingHandler = () => {
    setIsEditing(!isEditing)
  }

  const submitHandler = () => {
    const formValues = getValues()
    // only submit if all the values are filled out. they are all required
    if (Object.values(formValues).every(value => value)) {
      handleSubmit(updateQuote)()
    }
  }

  const buttonGroupValues: IButtonValueProps[] = [
    { value: 'all_in', label: 'All-In' },
    { value: 'linehaul', label: 'Linehaul' },
  ]

  const watchCustomerQuoteType = watch('customerQuoteType')
    .split('_')
    .map(word => {
      return word.charAt(0).toUpperCase() + word.slice(1)
    })
    .join('-')

  return (
    <Card className={className}>
      <CardHeader
        actionButton={
          !isEditing ? (
            <IconButton
              noPadding
              Icon={EditIcon}
              data-test='edit-quote'
              size='small'
              onClick={toggleEditingHandler}
              style={{ color: brandColors.skyBlue6 }}
            />
          ) : undefined
        }>
        <Typography variant='overline'>SUBMITTED QUOTE</Typography>
      </CardHeader>
      {loading && <Spinner />}
      {isEditing && !loading && (
        <div>
          <Box mb={2}>Enter the quote you submitted to the shipper here.</Box>
          <form>
            <Box display='flex' alignItems='flex-end' mb={2}>
              <ControlledTextField
                control={control}
                name='customerQuote'
                TextInputProps={{
                  label: `Submitted Quote - ${watchCustomerQuoteType}`,
                  required: true,
                }}
              />
              <ControlledButtonGroup
                control={control}
                name='customerQuoteType'
                buttons={buttonGroupValues}
              />
            </Box>
            <Box mb={2}>
              <ControlledTextField
                control={control}
                name='manualQuoteOverrideReason'
                TextInputProps={{
                  label: 'Reason For Adjustment',
                  placeholder: 'Write something...',
                  required: true,
                }}
              />
            </Box>
            <div>
              <Button data-test='submit-quote' onClick={submitHandler}>
                Save
              </Button>
              {!isMissingHistory && (
                <Button data-test='go-back-btn' color='tertiary' onClick={toggleEditingHandler}>
                  Go Back
                </Button>
              )}
            </div>
          </form>
        </div>
      )}
      {!isEditing && (
        <QuoteChanges quoteUpdates={submittedQuoteVersions} priceRequest={priceRequest} />
      )}
    </Card>
  )
}
